import React, { useCallback } from 'react';
import { clamp } from '../utils/fp-utils.js';
import styled from '../theme/styled.js';
import { token } from '../theme/tokens.js';

const QuantityControlContainer = styled.div({
    display: 'flex',
});

const QuantityControlInput = styled.input(({ theme }) => ({
    fontSize: token(theme, 'fontSize.stepper'),
    fontFamily: token(theme, 'font.bold'),
    fontWeight: 600,
    width: token(theme, 'size.stepperInputWidth'),
    background: token(theme, 'color.surface'),
    borderRadius: 0,
    padding: 0,
    height: token(theme, 'size.stepperHeight'),
    textAlign: 'center',
    border: `1px solid ${token(theme, 'color.borderSubtle')}`,
    borderWidth: '1px 0',
}));

const QuantityControlButton = styled.button(
    ({ theme }) => ({
        background: token(theme, 'color.surface'),
        border: `1px solid ${token(theme, 'color.borderSubtle')}`,
        color: token(theme, 'color.textMuted'),
        padding: 0,
        height: token(theme, 'size.stepperHeight'),
        width: token(theme, 'size.stepperInputWidth'),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'background-color 100ms ease-in-out',
        '&:nth-of-type(1)': {
            borderRight: 'none',
            borderTopLeftRadius: token(theme, 'radius.stepper'),
            borderBottomLeftRadius: token(theme, 'radius.stepper'),
        },
        '&:nth-of-type(2)': {
            borderLeft: 'none',
            borderTopRightRadius: token(theme, 'radius.stepper'),
            borderBottomRightRadius: token(theme, 'radius.stepper'),
        },
    }),
    ({ theme, disabled }) => ({
        ...disabled && {
            svg: {
                path: {
                    fill: `color-mix(in srgb, ${token(theme, 'color.textMuted')} 60%, transparent);`,
                },
            },
        },
        cursor: disabled ? 'not-allowed' : 'pointer',
        '&:hover': {
            backgroundColor: disabled ?
                token(theme, 'color.surface') :
                token(theme, 'color.accentSoft'),
        },
    }),
);

// Fill falls back to `currentColor` i.e. the font color of the surrounding text.
export const Minus = ({ theme }) => <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill={token(theme, 'icon.stepper') ?? 'currentColor'} fillRule="evenodd" clipRule="evenodd" d="M14 1C14 1.55229 13.5523 2 13 2L1 2C0.447715 2 -4.82823e-08 1.55228 0 1C4.82823e-08 0.447715 0.447715 -4.82823e-08 1 0L13 1.04907e-06C13.5523 1.09736e-06 14 0.447716 14 1Z" />
</svg>;

export const Plus = ({ theme }) => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill={token(theme, 'icon.stepper') ?? 'currentColor'} fillRule="evenodd" clipRule="evenodd" d="M8 1C8 0.447715 7.55228 0 7 0C6.44772 0 6 0.447715 6 1V6L1 6C0.447715 6 0 6.44771 0 7C0 7.55228 0.447715 8 1 8L6 8V13C6 13.5523 6.44771 14 7 14C7.55228 14 8 13.5523 8 13V8L13 8C13.5523 8 14 7.55228 14 7C14 6.44772 13.5523 6 13 6L8 6V1Z" />
</svg>;

export const QuantityInput = ({
    isLoading = false,
    itemLabel,
    maxValue = 999,
    minValue = 1,
    onChange,
    // Used to build the stepper's accessible names and the input's `id`, both of
    // which must be unique per product once more than one row can render.
    productCode,
    value,
}) => {
    const bound = useCallback(
        (v) => clamp(minValue, maxValue, v),
        [maxValue, minValue],
    );

    const handleChange = useCallback((event) => {
        if (event.target.value && !isNaN(event.target.value)) {
            onChange(bound(parseInt(event.target.value, 10)));
        }
    }, [onChange, bound]);

    const handleIncrement = useCallback(() => {
        onChange(bound(value + 1));
    }, [onChange, bound, value]);

    const handleDecrement = useCallback(() => {
        onChange(bound(value - 1));
    }, [onChange, bound, value]);

    const handleBlur = useCallback((event) => {
        if (!event.target.value) {
            onChange(minValue);
        }
    }, [onChange, minValue]);

    const onesPattern = new RegExp(`[${minValue}-9]`);

    const labelId = itemLabel ? ` for ${itemLabel}` : '';
    const inputId = `bec-quantity-${productCode ?? 'default'}`;

    return (
        <QuantityControlContainer>
            <QuantityControlButton
                type="button"
                className='trigger-gtm-quantity-decrement'
                aria-label={`Decrease quantity${labelId}`}
                aria-controls={inputId}
                onClick={handleDecrement}
                disabled={isLoading || value <= minValue}
            >
                <Minus/>
            </QuantityControlButton>
            <QuantityControlInput
                id={inputId}
                aria-label={`Quantity input${labelId}`}
                value={value || minValue}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}

                inputMode={'numeric'}
                /* Options below may not be needed? */
                guide={false}
                mask={[onesPattern, /[0-9]/, /[0-9]/]}
                min={minValue}
                max={maxValue}
            />
            <QuantityControlButton
                type="button"
                className='trigger-gtm-quantity-increment'
                aria-label={`Increase quantity${labelId}`}
                aria-controls={inputId}
                onClick={handleIncrement}
                disabled={isLoading || value >= maxValue}
            >
                <Plus/>
            </QuantityControlButton>
        </QuantityControlContainer>
    );
};
